import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import PrimaryButton from "../ui/buttons/primaryButton";
import Form from "../ui/forms";
import Select from "../ui/forms/fields/select";
import RadioGroup from "../ui/forms/fields/radioGroup";

import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  FormControlLabel,
  MenuItem,
  Radio,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Input from "../ui/forms/fields/input";
import { sendBespokeHFNotification } from "../../features/notification/notification.slice";
import OrangeCircularProgress from "../ui/progress/circularProgress";
import { useTheme } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import {
  getSubmition,
  resetSubmition,
} from "../../features/submition/submition.slice";
import { resetData } from "../../features/data/data.slice";

import { useGA4React } from "ga-4-react";
import {
  completeStep,
  resetSteps,
  updateStep,
} from "../../features/stepper.slice";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import { resetUser } from "../../features/user/user.slice";
import { Report } from "../../libs/loggers";

function HelpHedgeFunds(props) {
  const [open, setOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  let ga4React = useGA4React();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("md"));
  const submitionId = useSelector(
    (state) => state.submition.submition?.submitionId
  );
  const userId = useSelector((state) => state.submition.data?.userId);
  const [ammountAvailableOptions] = useState({
    qihf: [
      { key: "R1,000,000", value: "R1,000,000" },
      { key: "R1,000,000 - R5,000,000", value: "R1,000,000 - R5,000,000" },
      { key: "R5,000,000 - R10,000,000", value: "R5,000,000 - R10,000,000" },
      { key: "More than R10,000,000", value: "More than R10,000,000" },
    ],
    rihf: [
      { key: "R50,000 - R250,000", value: "R50,000 - R250,000" },
      { key: "R250,000 - R1,000,000", value: "R250,000 - R1,000,000" },
      { key: "R1,000,000 - R5,000,000", value: "R1,000,000 - R5,000,000" },
      { key: "More than R5,000,000", value: "More than R5,000,000" },
    ],
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  let page = window.location.pathname.replace(/^\/|\/$/g, "");
  const vehicle = useSelector((state) => state.data.value.vehicle);
  const notificationSuccess = useSelector(
    (state) => state.notification.notification.bespokeHFNotificationSuccess
  );
  const loading = useSelector(
    (state) => state.notification.notification.loading
  );

  const stepper = useSelector((state) => state.stepper.value);

  const validationSchema = yup.object().shape({
    amountAvailable: yup
      .string()
      .required("Please the amount you have available to invest."),
    amountDirectlyInvested: yup.string().required("Please select one."),
    objective: yup.string().required("Please select one."),
    name: yup.string().required("Please provide us with your name."),
    email: yup.string().required("Please provide us with your email address."),
    phoneNumber: yup
      .string()
      .required("Please provide us with your phone number."),
  });

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm({
    mode: ["onBlur"],
    resolver: yupResolver(validationSchema),
  });

  const handleClickOpen = () => {
    if (ga4React && vehicle) {
      ga4React.event(
        "requested_something_bespoke",
        `${vehicle}`,
        "help_me_find_my_fund"
      );
    }
    setOpen(true);
  };

  const handleBack = () => {
    dispatch(updateStep(stepper.active - 1));
    navigate(stepper.steps[stepper.active - 1].url);

    ga4React.event(
      "prev_step",
      stepper.steps[stepper.active - 1].name,
      `Step ${stepper.active - 1}`
    );
  };

  const goToWebsite = () => {
    const newStep = stepper.active + 1;
    const eventInfo = {
      action: "click",
      category: "guidance",
      description: "clicked go to website",
      value: {
        nextPage: stepper.steps[newStep].url,
        prevPage: page,
        step: stepper.active + 1,
        value: "go_to_site",
      },
    };
    Report(eventInfo, userId, submitionId);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleAction = () => {
    handleSubmit(onSubmit)();
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
    const eventInfo = {
      action: "click",
      category: "guidance",
      description: `clicked start another investement`,
      value: {
        nextPage: stepper.steps[0].url,
        prevPage: page,
        step: stepper.active + 1,
        value: `start_another`,
      },
    };

    Report(eventInfo, userId, submitionId);
    navigate(stepper.steps[0].url);
    dispatch(resetSubmition());
    dispatch(resetData());
    dispatch(resetSteps());
    dispatch(resetUser());
  };

  const onSubmit = (data) => {
    const eventInfo = {
      action: "submit",
      category: "help",
      description: `submitted information requiring help`,
      value: {
        currentPage: page,
        step: stepper.active + 1,
        amountAvailable: data.amountAvailable,
        amountDirectlyInvested: data.amountDirectlyInvested,
        objective: data.objective,
        value: `submit`,
      },
    };
    Report(eventInfo, userId, submitionId);
    setAlertOpen(true);
    data.hfType = props.hfType;
    data.vehicle = vehicle;
    dispatch(sendBespokeHFNotification(data));
    setOpen(false);
  };

  return (
    <Fragment>
      <Form>
        <Select
          name={"amountAvailable"}
          control={control}
          error={!!errors.amountAvailable}
          helperText={errors.amountAvailable?.message}
          label={
            mobile
              ? "I have the following available to invest:"
              : "I have the following amount available to invest:"
          }
        >
          {props.hfType === "QIHF"
            ? ammountAvailableOptions.qihf.map((item, idx) => (
                <MenuItem key={idx} value={item.key}>
                  {item.value}
                </MenuItem>
              ))
            : ammountAvailableOptions.rihf.map((item, idx) => (
                <MenuItem key={idx} value={item.key}>
                  {item.value}
                </MenuItem>
              ))}
        </Select>

        <Typography variant="h6" sx={{ mt: 2 }}>
          A portion of the amount I've indicated in Question 1, is directly
          invested in a share portfolio.
        </Typography>
        <RadioGroup
          control={control}
          aria-label="amountDirectlyInvested"
          name="amountDirectlyInvested"
          error={!!errors?.amountDirectlyInvested}
          helperText={errors?.amountDirectlyInvested?.message}
          xs={{ margin: 1 }}
        >
          <FormControlLabel
            value={"Yes"}
            control={<Radio />}
            color="primary"
            label={"Yes"}
          />
          <FormControlLabel
            value={"No"}
            control={<Radio />}
            color="primary"
            label={"No"}
          />
        </RadioGroup>
        <br />
        <Typography variant="h6" sx={{ mt: 2 }}>
          My main objective with this investment is:
        </Typography>
        <RadioGroup
          control={control}
          aria-label="objective"
          name="objective"
          error={!!errors?.objective}
          helperText={errors?.objective?.message}
          xs={{ margin: 1 }}
        >
          <FormControlLabel
            value={
              "I want to diversify my existing investment portfolio to include alternative assets."
            }
            control={<Radio />}
            color="primary"
            label={
              "I want to diversify my existing investment portfolio to include alternative assets."
            }
            sx={mobile ? { mt: 1, mb: 1 } : {}}
          />
          <FormControlLabel
            value={
              "I'm looking for a bespoke solution to enhance my existing investment portfolio. "
            }
            control={<Radio />}
            color="primary"
            label={
              "I'm looking for a bespoke solution to enhance my existing investment portfolio. "
            }
            sx={mobile ? { mt: 1, mb: 1 } : {}}
          />
          <FormControlLabel
            value={
              "As a high net worth investor, I want to further grow my wealth, by investing in niche products."
            }
            control={<Radio />}
            color="primary"
            label={
              "As a high net worth investor, I want to further grow my wealth, by investing in niche products."
            }
            sx={mobile ? { mt: 1, mb: 1 } : {}}
          />
        </RadioGroup>

        <Typography variant="h6" sx={{ mt: 2 }}>
          Please provide us with your details:
        </Typography>
        <Input label="Your Name" {...register("name")} error={!!errors.name} />
        <Input
          label="Email Address"
          {...register("email")}
          error={!!errors.email}
        />
        <Input
          label="Phone Number"
          {...register("phoneNumber")}
          error={!!errors.phoneNumber}
        />

        <Box display={"flex"} justifyContent={"flex-end"}>
          <PrimaryButton variant="outlined" onClick={handleBack}>
            Cancel
          </PrimaryButton>
          <PrimaryButton onClick={handleAction}>Submit</PrimaryButton>
        </Box>
      </Form>

      <Dialog open={alertOpen} onClose={handleAlertClose}>
        <DialogContent>
          {loading ? (
            <OrangeCircularProgress caption={"Sending Request"} />
          ) : notificationSuccess ? (
            <Box textAlign={"center"}>
              <TaskAltOutlinedIcon
                color={"success"}
                sx={{ margin: "auto", mt: 3, width: "200px", height: "200px" }}
              />
              <Typography variant="h4" sx={{ pb: 0.1, textAlign: "center" }}>
                Thank you for your submission. We have received your query and
                will get back to you.
              </Typography>
            </Box>
          ) : (
            <Typography variant="h4" sx={{ pb: 0.1 }}>
              Something went wrong, please try again later.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
          <PrimaryButton
            onClick={() => goToWebsite()}
            href={"https://fairtree.com/"}
            target="_blank"
          >
            Go to website
          </PrimaryButton>
          <PrimaryButton onClick={() => handleAlertClose()}>
            Start another investment
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}

export default HelpHedgeFunds;
